import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  UserIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { useState, useEffect } from 'react'
import ResetPasswordModal from '../components/users/ResetPasswordModal'
import UserModal from '../components/users/UserModal'
import { useAuth } from '../contexts/AuthContext'
import { userAPI } from '../services/api'
import { User } from '../types'

type SortColumn = 'name' | 'email' | 'role' | 'status' | 'last_login'
type SortDirection = 'asc' | 'desc'

interface SortState {
  column: SortColumn
  direction: SortDirection
}

export default function UsersPage() {
  const { user: currentUser, impersonateUser } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showUserModal, setShowUserModal] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)

  // Sorting state - default sort by name
  const [sortState, setSortState] = useState<SortState>({
    column: 'name',
    direction: 'asc',
  })

  useEffect(() => {
    fetchUsers()
  }, [sortState])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await userAPI.list()

      // Sort users based on current sort state
      const sortedUsers = [...response.data].sort((a, b) => {
        let aValue: any, bValue: any

        switch (sortState.column) {
          case 'name':
            aValue = a.name.toLowerCase()
            bValue = b.name.toLowerCase()
            break
          case 'email':
            aValue = a.email.toLowerCase()
            bValue = b.email.toLowerCase()
            break
          case 'role':
            aValue = a.is_admin ? 1 : 0
            bValue = b.is_admin ? 1 : 0
            break
          case 'status':
            aValue = a.is_active ? 1 : 0
            bValue = b.is_active ? 1 : 0
            break
          case 'last_login':
            aValue = a.last_login ? new Date(a.last_login).getTime() : 0
            bValue = b.last_login ? new Date(b.last_login).getTime() : 0
            break
          default:
            return 0
        }

        if (sortState.direction === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
        }
      })

      setUsers(sortedUsers)
    } catch (error) {
      console.error('Error fetching users:', error)
      alert('Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUser = async (userData: any) => {
    try {
      await userAPI.create(userData)
      await fetchUsers()
      setShowUserModal(false)
      alert('User created successfully. They can login with their email as temporary password.')
    } catch (error: any) {
      if (error.response?.status === 400) {
        alert(error.response.data.detail)
      } else {
        alert('Failed to create user')
      }
    }
  }

  const handleUpdateUser = async (userData: any) => {
    if (!selectedUser) return

    try {
      await userAPI.update(selectedUser.id, userData)
      await fetchUsers()
      setShowUserModal(false)
      setSelectedUser(null)
    } catch (error: any) {
      if (error.response?.status === 400) {
        alert(error.response.data.detail)
      } else {
        alert('Failed to update user')
      }
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return

    try {
      await userAPI.delete(userId)
      await fetchUsers()
    } catch (error: any) {
      if (error.response?.status === 400) {
        alert(error.response.data.detail)
      } else {
        alert('Failed to delete user')
      }
    }
  }

  const handleResetPassword = async (userId: string, temporaryPassword: string) => {
    try {
      await userAPI.resetPassword(userId, temporaryPassword)
      setShowResetModal(false)
      setSelectedUser(null)
      alert('Password reset successfully. The user can login with the temporary password.')
    } catch (error) {
      alert('Failed to reset password')
    }
  }

  const openEditModal = (user: User) => {
    setSelectedUser(user)
    setShowUserModal(true)
  }

  const openResetModal = (user: User) => {
    setSelectedUser(user)
    setShowResetModal(true)
  }

  const handleImpersonate = async (userId: string) => {
    if (!confirm('Are you sure you want to impersonate this user?')) return

    try {
      await impersonateUser(userId)
    } catch (error) {
      alert('Failed to impersonate user')
    }
  }

  const handleColumnSort = (column: SortColumn) => {
    if (sortState.column === column) {
      // Toggle direction
      setSortState({
        column,
        direction: sortState.direction === 'asc' ? 'desc' : 'asc',
      })
    } else {
      // New column, default to ascending
      setSortState({
        column,
        direction: 'asc',
      })
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Loading users...</p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
        <button
          onClick={() => {
            setSelectedUser(null)
            setShowUserModal(true)
          }}
          className="btn-primary flex items-center"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          New User
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleColumnSort('name')}
              >
                <div className="flex items-center">
                  <span>Name</span>
                  {sortState.column === 'name' && (
                    <span className="ml-2">
                      {sortState.direction === 'asc' ? (
                        <ChevronUpIcon className="w-4 h-4" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleColumnSort('email')}
              >
                <div className="flex items-center">
                  <span>Email</span>
                  {sortState.column === 'email' && (
                    <span className="ml-2">
                      {sortState.direction === 'asc' ? (
                        <ChevronUpIcon className="w-4 h-4" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleColumnSort('role')}
              >
                <div className="flex items-center">
                  <span>Role</span>
                  {sortState.column === 'role' && (
                    <span className="ml-2">
                      {sortState.direction === 'asc' ? (
                        <ChevronUpIcon className="w-4 h-4" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleColumnSort('status')}
              >
                <div className="flex items-center">
                  <span>Status</span>
                  {sortState.column === 'status' && (
                    <span className="ml-2">
                      {sortState.direction === 'asc' ? (
                        <ChevronUpIcon className="w-4 h-4" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleColumnSort('last_login')}
              >
                <div className="flex items-center">
                  <span>Last Login</span>
                  {sortState.column === 'last_login' && (
                    <span className="ml-2">
                      {sortState.direction === 'asc' ? (
                        <ChevronUpIcon className="w-4 h-4" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4" />
                      )}
                    </span>
                  )}
                </div>
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map(user => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{user.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{user.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.is_admin
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {user.is_admin ? 'Admin' : 'User'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {user.first_login && (
                    <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      First Login
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center justify-end space-x-3">
                    {user.id !== currentUser?.id && !user.is_admin && user.is_active ? (
                      <button
                        onClick={() => handleImpersonate(user.id)}
                        className="text-purple-600 hover:text-purple-900"
                        title="Impersonate User"
                      >
                        <UserIcon className="w-5 h-5" />
                      </button>
                    ) : (
                      <div className="w-5 h-5" />
                    )}
                    <button
                      onClick={() => openEditModal(user)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Edit"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => openResetModal(user)}
                      className="text-orange-600 hover:text-orange-900"
                      title="Reset Password"
                    >
                      <KeyIcon className="w-5 h-5" />
                    </button>
                    {user.id !== currentUser?.id ? (
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    ) : (
                      <div className="w-5 h-5" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showUserModal && (
        <UserModal
          user={selectedUser}
          onClose={() => {
            setShowUserModal(false)
            setSelectedUser(null)
          }}
          onSave={selectedUser ? handleUpdateUser : handleCreateUser}
        />
      )}

      {showResetModal && selectedUser && (
        <ResetPasswordModal
          user={selectedUser}
          onClose={() => {
            setShowResetModal(false)
            setSelectedUser(null)
          }}
          onReset={handleResetPassword}
        />
      )}
    </div>
  )
}
